import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import React from 'react';

import { InspectorBody } from './InspectorBody';
import type { InspectorProps, Presentation } from './types';

import {
  Dialog,
  DialogContent,
  DialogContentContainerContext,
  DialogTitle,
  usePopperAwareFocusOutside,
  usePopperAwarePointerDownOutside,
} from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { useEscapeRevertGuard } from '@/lib/keyboard';
import { cn } from '@/lib/utils';

/**
 * Inspector body renders its own visible heading — the modal/sheet shell
 * needs a Radix Title for screen readers, but a second visible title would
 * duplicate the heading. Wrap a generic label in VisuallyHidden so the a11y
 * tree gets a label without doubling up visible chrome.
 */
export function inspectorA11yLabel(kind: InspectorProps['subject']['kind']): string {
  if (kind === 'concept_design') return 'Concept design workspace';
  const words = kind.replaceAll('_', ' ');
  return `${words.charAt(0).toUpperCase()}${words.slice(1)} inspector`;
}

/**
 * Resolve the effective presentation given the caller's hint and the viewport.
 * Mobile collapses both `modal` and `drawer` to `fullscreen` for usability —
 * a side drawer or centered modal on a 390px viewport is too cramped.
 */
function resolveEffective(presentation: Presentation, isMobile: boolean): Presentation {
  if (isMobile && presentation !== 'workspace') return 'fullscreen';
  return presentation;
}

const WORKSPACE_EXIT_MS = 200;

/**
 * Inspector — Mora's canonical "look at and edit this thing" primitive.
 *
 * One primitive, polymorphic body dispatched on `subject.kind`, three
 * presentation shells selected by the caller.
 *
 * Spec: docs/superpowers/specs/2026-04-26-inspector-primitive-and-surface-unification-design.md
 */
export const Inspector: React.FC<InspectorProps> = ({
  subject,
  open,
  onOpenChange,
  presentation = 'modal',
  className,
  breadcrumbs,
  studioPanel,
  backToProject,
}) => {
  const isMobile = useIsMobile();
  const effective = resolveEffective(presentation, isMobile);
  const [workspaceClosing, setWorkspaceClosing] = React.useState(false);
  const workspaceCloseTimerRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (open && effective === 'workspace') {
      setWorkspaceClosing(false);
    }
  }, [effective, open, subject]);

  React.useEffect(
    () => () => {
      if (workspaceCloseTimerRef.current) {
        window.clearTimeout(workspaceCloseTimerRef.current);
      }
    },
    [],
  );

  const requestWorkspaceOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        if (workspaceCloseTimerRef.current) {
          window.clearTimeout(workspaceCloseTimerRef.current);
          workspaceCloseTimerRef.current = null;
        }
        setWorkspaceClosing(false);
        onOpenChange(true);
        return;
      }

      if (workspaceClosing) return;
      setWorkspaceClosing(true);
      workspaceCloseTimerRef.current = window.setTimeout(() => {
        workspaceCloseTimerRef.current = null;
        setWorkspaceClosing(false);
        onOpenChange(false);
      }, WORKSPACE_EXIT_MS);
    },
    [onOpenChange, workspaceClosing],
  );

  // Workspace mode renders raw DialogPrimitive.Content (not the wrapped
  // DialogContent from @/components/ui/dialog), so it needs these guards
  // applied directly — DialogContent normally supplies both automatically.
  // Without the pointer-down guard, a nested Popover/DropdownMenu opened from
  // inside the inspector body (e.g. PostBody's "Add to project" popover)
  // cascades this whole dialog closed when the user clicks outside it. Same
  // for focus-outside: a nested Popper's content portals to document.body —
  // a DOM sibling of this Content, not a descendant — so focus moving into
  // it reads as "focus left the dialog" without this guard too.
  // Root-cause fix (not just the guards above): give nested DropdownMenu/
  // Select/Popover a container INSIDE this Content's own DOM subtree so
  // they portal there instead of to document.body. A real DOM descendant
  // can never be misread as "outside" by Radix's native dismiss checks —
  // see DialogContentContainerContext's doc comment in dialog.tsx.
  const [workspaceContainer, setWorkspaceContainer] = React.useState<HTMLDivElement | null>(null);

  /**
   * 🔴 THE THIRD GUARD, and the one the first two could not cover.
   *
   * Measured on the Concept Workbench, 2026-08-12: open ANY nested menu (the
   * Layers `+`, the header `…`, an inspector property dropdown — all of which
   * already portal into `workspaceContainer`), then click once on the canvas.
   * The menu dismissed on pointerdown, and the WHOLE workspace dismissed
   * immediately after. Traced to `usePointerDownOutside → onDismiss` on this
   * dialog, with the custom event's target resolving to `<html>`.
   *
   * Two things combine to produce it. A modal layer sets `pointer-events: none`
   * on the body, so `elementFromPoint` under a still-closing layer resolves to
   * `<html>` rather than to the canvas that is visibly there — genuinely
   * "outside" this Content by containment. And `isNestedPopperOrPortalOpen`
   * cannot save it: the menu is the TOP layer, so it handles the same
   * pointerdown first and unmounts its popper wrapper, and by the time this
   * dialog's handler runs there is no open popper left to detect.
   *
   * So containment is asked GEOMETRICALLY instead of by DOM ancestry: a
   * pointerdown whose coordinates land inside this Content's own rectangle is
   * not a click outside it, whatever the browser resolved under a
   * pointer-events-disabled body. The workspace covers `inset-0` of
   * `SidebarInset`, so the only region this still treats as outside is the app
   * sidebar — which is exactly the region where dismissing is correct.
   *
   * Part 1's commit message claimed this path was "TESTED, not assumed". It was
   * not: Escape and a synthetic dispatch both survive, and only a real
   * pointerdown/pointerup pair reproduces it.
   */
  const workspaceGeometricGuard = React.useCallback(
    (event: CustomEvent<{ originalEvent: PointerEvent }>) => {
      const node = workspaceContainer;
      if (!node) return;
      const { clientX, clientY } = event.detail.originalEvent;
      const bounds = node.getBoundingClientRect();
      const inside =
        clientX >= bounds.left &&
        clientX <= bounds.right &&
        clientY >= bounds.top &&
        clientY <= bounds.bottom;
      if (inside) event.preventDefault();
    },
    [workspaceContainer],
  );

  const workspacePointerDownOutside = usePopperAwarePointerDownOutside(workspaceGeometricGuard);
  const workspaceFocusOutside = usePopperAwareFocusOutside(undefined);
  const workspaceEscapeKeyDown = useEscapeRevertGuard(undefined);

  if (effective === 'workspace') {
    return (
      <Dialog open={open} onOpenChange={requestWorkspaceOpenChange}>
        <DialogPrimitive.Content
          ref={setWorkspaceContainer}
          data-workspace-inspector="true"
          data-closing={workspaceClosing ? 'true' : undefined}
          className={cn(
            'absolute inset-0 z-40 flex min-h-0 origin-center flex-col bg-background outline-none will-change-transform',
            workspaceClosing && 'pointer-events-none',
            className,
          )}
          aria-describedby={undefined}
          onPointerDownOutside={workspacePointerDownOutside}
          onFocusOutside={workspaceFocusOutside}
          onEscapeKeyDown={workspaceEscapeKeyDown}
        >
          <VisuallyHidden.Root>
            <DialogTitle>{inspectorA11yLabel(subject.kind)}</DialogTitle>
          </VisuallyHidden.Root>
          <DialogContentContainerContext.Provider value={workspaceContainer}>
            <InspectorBody
              subject={subject}
              onClose={() => requestWorkspaceOpenChange(false)}
              breadcrumbs={breadcrumbs}
              studioPanel={studioPanel}
              backToProject={backToProject}
            />
          </DialogContentContainerContext.Provider>
        </DialogPrimitive.Content>
      </Dialog>
    );
  }

  if (effective === 'drawer') {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          hideCloseButton
          className={cn('p-0 gap-0 w-full sm:max-w-[480px]', className)}
        >
          <VisuallyHidden.Root>
            <SheetTitle>{inspectorA11yLabel(subject.kind)}</SheetTitle>
          </VisuallyHidden.Root>
          <InspectorBody
            subject={subject}
            onClose={() => onOpenChange(false)}
            breadcrumbs={breadcrumbs}
            studioPanel={studioPanel}
            backToProject={backToProject}
          />
        </SheetContent>
      </Sheet>
    );
  }

  if (effective === 'fullscreen') {
    // On mobile this is a full-height bottom sheet. On desktop it's a
    // wide Dialog (size=full, ~6xl) for the explicit "expand to focus" mode.
    if (isMobile) {
      return (
        <Sheet open={open} onOpenChange={onOpenChange}>
          <SheetContent
            side="bottom"
            hideCloseButton
            className={cn('flex h-[92vh] flex-col p-0 gap-0 rounded-t-2xl', className)}
          >
            <VisuallyHidden.Root>
              <SheetTitle>{inspectorA11yLabel(subject.kind)}</SheetTitle>
            </VisuallyHidden.Root>
            {/* iOS-style sheet grabber — signals the sheet can be dragged/dismissed. */}
            <div className="flex shrink-0 justify-center pb-1 pt-2.5" aria-hidden>
              <div className="h-1 w-9 rounded-full bg-muted-foreground/25" />
            </div>
            <InspectorBody
              subject={subject}
              onClose={() => onOpenChange(false)}
              breadcrumbs={breadcrumbs}
              studioPanel={studioPanel}
            />
          </SheetContent>
        </Sheet>
      );
    }
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent size="full" className={cn('p-0 gap-0 max-h-[90vh]', className)}>
          <VisuallyHidden.Root>
            <DialogTitle>{inspectorA11yLabel(subject.kind)}</DialogTitle>
          </VisuallyHidden.Root>
          <InspectorBody
            subject={subject}
            onClose={() => onOpenChange(false)}
            breadcrumbs={breadcrumbs}
            studioPanel={studioPanel}
            backToProject={backToProject}
          />
        </DialogContent>
      </Dialog>
    );
  }

  // Modal mode (default). Image, asset, and post kinds all carry a complex
  // internal layout (asset stage / post preview + 480px studio rail) that
  // needs ≥1100px of canvas to read. `size="lg"` (max-w-2xl = 672px) crams
  // the body and leaves a strip of the right-side ArtifactDrawer visible at
  // the edges — that's the "modal coming out of the strategy panel" bug.
  // `size="full"` (max-w-6xl = 1152px) makes the modal dominate the
  // viewport so it visually replaces the right rail rather than competing
  // with it. Document subjects keep the narrower lg treatment.
  const isWideSubject =
    subject.kind === 'image' || subject.kind === 'post' || subject.kind === 'video';
  // Only image/video actually need a FIXED height — their asset stage fills
  // available space via object-contain regardless of the media's own size.
  // Post bodies (caption + media strip + editing rail) are naturally
  // variable-height; forcing the same fixed height here left short-caption
  // posts with a large dead gap between the content and the pinned composer
  // (caught in live review) instead of hugging their content up to the cap.
  const isFixedHeightSubject = subject.kind === 'image' || subject.kind === 'video';
  // Document / strategy / brand-guidelines / project subjects render long
  // markdown bodies that need an internal scroll. Cap the modal at 85vh and
  // force `flex flex-col` so the body's `flex-1 overflow-y-auto` actually
  // computes a bounded height. Without these, the dialog grows past the
  // viewport and the document overflows top + bottom with no scroll.
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size={isWideSubject ? 'full' : 'lg'}
        responsive
        // hideCloseButton: ImageBody/PostBody render their own inline close
        // button in the right-rail header. The radix-default floating X
        // would visually duplicate it.
        hideCloseButton
        className={cn(
          // overflow-hidden + the rounded-xl from base Dialog = children
          // (incl. the bg-canvas asset column) get clipped to the rounded
          // shape. Without this, sharp asset-column corners stuck out
          // past the dialog's rounded mask on the left edge.
          'flex max-h-[85vh] flex-col p-0 gap-0 overflow-hidden',
          // size="full" (max-w-6xl=1152) made the post/image inspector balloon on
          // wide screens — the preview floated in dead space and the rail read
          // oversized. Cap it ~1000px so the two panes stay balanced.
          isWideSubject && 'sm:max-w-[1000px]',
          // Fixed height (not just a cap) for image/video — the asset stage
          // fills whatever the dialog gives it via flex-1, so without a
          // DEFINITE height here the dialog shrinks to fit a small image on
          // first paint, then visibly jumps once the image's natural size (or
          // the LQIP/skeleton) resolves. A stable canvas means the dialog
          // never resizes based on what's inside it.
          isFixedHeightSubject && 'h-[85vh] min-h-[600px]',
          className,
        )}
      >
        <VisuallyHidden.Root>
          <DialogTitle>{inspectorA11yLabel(subject.kind)}</DialogTitle>
        </VisuallyHidden.Root>
        <InspectorBody
          subject={subject}
          onClose={() => onOpenChange(false)}
          breadcrumbs={breadcrumbs}
          studioPanel={studioPanel}
          backToProject={backToProject}
        />
      </DialogContent>
    </Dialog>
  );
};
